import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    MagnifyingGlass, Plus, Minus, Trash, Money, CreditCard, Receipt, X, Barcode, ChartLineUp,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import ScannerDialog from "@/admin/components/ScannerDialog";
import DailyReportDialog from "@/admin/components/DailyReportDialog";
import printReceipt from "@/lib/printReceipt";
import {
    printOrder, subscribePrinterState, getState as getBtState,
} from "@/lib/btThermal";

const TAX_RATE = 0.0735; // J&W Deli sales tax (taxable products only)

export default function POS() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [activeCat, setActiveCat] = useState("all");
    const [cart, setCart] = useState([]); // [{id,name,price,qty}]
    const [busy, setBusy] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [cashOpen, setCashOpen] = useState(false);
    const [cashReceived, setCashReceived] = useState("");
    const [customName, setCustomName] = useState("");
    const [customPrice, setCustomPrice] = useState("");
    const [customDialog, setCustomDialog] = useState(false);
    const [lastReceipt, setLastReceipt] = useState(null);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [discount, setDiscount] = useState("");
    const [tip, setTip] = useState("");
    const [terminal, setTerminal] = useState(() => localStorage.getItem("jw_terminal") || "");
    useEffect(() => { localStorage.setItem("jw_terminal", terminal); }, [terminal]);
    // Printer preferences (saved in localStorage so each device remembers).
    const [printerWidth, setPrinterWidth] = useState(() =>
        Number(localStorage.getItem("jw_printer_width")) || 58,
    );
    const [autoPrint, setAutoPrint] = useState(
        () => localStorage.getItem("jw_pos_autoprint") === "on",
    );

    useEffect(() => {
        localStorage.setItem("jw_printer_width", String(printerWidth));
    }, [printerWidth]);
    useEffect(() => {
        localStorage.setItem("jw_pos_autoprint", autoPrint ? "on" : "off");
    }, [autoPrint]);

    // Bluetooth printer state — pairing & testing happen in /admin/printer-settings.
    const [btState, setBtState] = useState(getBtState());
    useEffect(() => subscribePrinterState(setBtState), []);

    // Returning from Stripe Checkout (POS link) — confirm payment & mark order paid.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get("session_id");
        const st = params.get("stripe_status");
        if (!st) return;
        window.history.replaceState({}, "", window.location.pathname);
        if (st !== "success" || !sid) {
            toast.info("Pago con Stripe cancelado");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await api.get(`/checkout/status/${sid}`);
                if (r.data.payment_status === "paid") {
                    toast.success("Pago Stripe confirmado ✓");
                    return;
                }
            } catch { /* keep polling */ }
            if (attempts < 8) setTimeout(poll, 2000);
            else toast.error("No se pudo confirmar el pago Stripe. Revisa Pedidos.");
        };
        poll();
    }, []);


    // Smart print: try Bluetooth first, fall back to OS print dialog.
    const smartPrint = async (order, kind = "customer") => {
        if (btState.connected) {
            try {
                await printOrder(order, { kind });
                return;
            } catch (err) {
                console.warn("BT print failed, falling back to HTML", err);
                toast.error("Bluetooth falló · imprimiendo por sistema");
            }
        }
        printReceipt(order, { kind, width: printerWidth });
    };

    useEffect(() => {
        api.get("/admin/menu", { headers: authHeaders() })
            .then((r) => setItems(r.data.items.filter((i) => i.available)))
            .catch(() => toast.error("Couldn't load menu"));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(items.map((i) => i.category).filter(Boolean));
        return ["all", ...Array.from(set).sort()];
    }, [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((i) => {
            if (activeCat !== "all" && i.category !== activeCat) return false;
            if (!q) return true;
            return (
                (i.name_en || "").toLowerCase().includes(q) ||
                (i.name_es || "").toLowerCase().includes(q)
            );
        });
    }, [items, search, activeCat]);

    const addToCart = (it) => {
        setCart((prev) => {
            const idx = prev.findIndex((p) => p.id === it.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
                return next;
            }
            return [...prev, { id: it.id, name: it.name_en, price: it.price, qty: 1, taxable: it.taxable !== false }];
        });
    };

    const onScan = async (code, source) => {
        const c = (code || "").trim();
        if (!c) return;
        // First try local match — instant, no network roundtrip.
        const local = items.find(
            (i) => i.barcode === c || i.id === c,
        );
        if (local) {
            addToCart(local);
            toast.success(`+ ${local.name_en}`);
            return;
        }
        // Fall back to server lookup (covers items not yet in client cache,
        // and 86'd items that were filtered out of `items`).
        try {
            const r = await api.get(`/admin/menu/lookup/${encodeURIComponent(c)}`, {
                headers: authHeaders(),
            });
            addToCart(r.data);
            toast.success(`+ ${r.data.name_en}`);
        } catch (e) {
            if (e?.response?.status === 404) {
                toast.error(`Código no reconocido: ${c}`, {
                    description: `Escáner ${source === "cam" ? "(cámara)" : "(hardware)"}`,
                });
            } else {
                toast.error("Error al buscar el código");
            }
        }
        // Keep scanner open if hardware (cashier can scan multiple), close if camera.
        if (source === "cam") setScannerOpen(false);
    };

    const addCustom = () => {
        const price = parseFloat(customPrice);
        if (!customName.trim() || !(price >= 0)) {
            toast.error("Llena nombre y precio");
            return;
        }
        setCart((prev) => [
            ...prev,
            { id: "custom", name: customName.trim(), price: round2(price), qty: 1, custom: true },
        ]);
        setCustomName("");
        setCustomPrice("");
        setCustomDialog(false);
    };

    const inc = (idx) =>
        setCart((prev) => prev.map((p, i) => (i === idx ? { ...p, qty: p.qty + 1 } : p)));
    const dec = (idx) =>
        setCart((prev) =>
            prev
                .map((p, i) => (i === idx ? { ...p, qty: p.qty - 1 } : p))
                .filter((p) => p.qty > 0),
        );
    const remove = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
    const clear = () => { setCart([]); setDiscount(""); setTip(""); };

    const subtotal = round2(cart.reduce((s, c) => s + c.price * c.qty, 0));
    const taxableSubtotal = round2(cart.reduce((s, c) => s + (c.taxable === false ? 0 : c.price * c.qty), 0));
    const discountAmt = Math.min(subtotal, Math.max(0, parseFloat(discount) || 0));
    const tipAmt = Math.max(0, parseFloat(tip) || 0);
    const taxableBase = taxableSubtotal - (subtotal ? discountAmt * (taxableSubtotal / subtotal) : 0);
    const tax = round2(Math.max(0, taxableBase) * TAX_RATE);
    const total = round2(subtotal - discountAmt + tax + tipAmt);

    const send = async (method, extra = {}) => {
        if (cart.length === 0) {
            toast.error("Carrito vacío");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                items: cart.map((c) => ({
                    id: c.id,
                    qty: c.qty,
                    ...(c.id === "custom" ? { name: c.name, unit_price: c.price } : {}),
                })),
                payment_method: method,
                fulfillment: "pickup",
                customer_name: "Walk-in",
                discount: discountAmt,
                tip: tipAmt,
                ...(terminal.trim() ? { terminal: terminal.trim() } : {}),
                ...extra,
            };
            const r = await api.post("/admin/pos/order", payload, { headers: authHeaders() });
            if (method === "stripe" && r.data.checkout_url) {
                // Open Stripe Checkout in a new window so the cashier can show
                // the page to the customer or scan the URL as a QR.
                window.open(r.data.checkout_url, "_blank", "noopener,noreferrer");
                toast.success("Stripe Checkout abierto · sigue al cliente");
                setPayOpen(false);
                clear();
            } else {
                setLastReceipt(r.data.order);
                if (autoPrint) {
                    // Fire customer receipt then kitchen ticket. Uses Bluetooth
                    // when paired, otherwise OS print dialog.
                    await smartPrint(r.data.order, "customer");
                    setTimeout(() => smartPrint(r.data.order, "kitchen"), 400);
                }
                toast.success(method === "cash" ? "Pago en efectivo registrado" : "Pago con tarjeta registrado");
                setPayOpen(false);
                setCashOpen(false);
                setCashReceived("");
                clear();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al procesar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-pos">
            <PageHeader kicker="Cobro en tienda" title="POS — Punto de Venta" />

            {/* Printer toolbar */}
            <div
                data-testid="pos-printer-bar"
                className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-2xl bg-muted/50 border border-border text-xs"
            >
                {btState.supported ? (
                    <span
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border-2 ${
                            btState.connected
                                ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                                : "border-border bg-white text-foreground/60"
                        }`}
                        data-testid="bt-status"
                    >
                        <span
                            className={`h-2 w-2 rounded-full ${
                                btState.connected ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"
                            }`}
                        />
                        {btState.connected
                            ? `BT · ${btState.name || "Conectada"}`
                            : "BT desconectada"}
                    </span>
                ) : (
                    <span className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border-2 border-amber-300 bg-amber-50 text-amber-700">
                        Sin Bluetooth · usa Chrome / Edge
                    </span>
                )}

                <a
                    data-testid="open-printer-settings"
                    href="/admin/printer-settings"
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border-2 border-border bg-white hover:bg-muted"
                >
                    Ajustes de impresora →
                </a>

                <span className="h-5 w-px bg-border hidden sm:block" />

                <label className="inline-flex items-center gap-2" data-testid="pos-terminal-field">
                    <span className="font-bold uppercase tracking-widest text-[10px]">Terminal</span>
                    <input
                        data-testid="pos-terminal-input"
                        value={terminal}
                        onChange={(e) => setTerminal(e.target.value)}
                        placeholder="T1"
                        className="h-8 w-24 rounded-full border-2 border-border bg-white px-3 text-xs font-bold focus:outline-none focus:border-foreground"
                    />
                </label>

                <span className="h-5 w-px bg-border hidden sm:block" />

                <label
                    data-testid="autoprint-toggle"
                    className="inline-flex items-center gap-2 cursor-pointer select-none"
                >
                    <input
                        type="checkbox"
                        checked={autoPrint}
                        onChange={(e) => setAutoPrint(e.target.checked)}
                        className="h-4 w-4 accent-primary"
                    />
                    <span className="font-bold uppercase tracking-widest text-[10px]">
                        Imprimir al cobrar
                    </span>
                </label>

                <div className="ml-auto">
                    <Button
                        data-testid="pos-report-btn"
                        onClick={() => setReportOpen(true)}
                        className="rounded-full h-9 bg-foreground hover:bg-foreground/90 text-background font-bold uppercase tracking-widest text-[11px]"
                    >
                        <ChartLineUp size={14} weight="bold" className="mr-1.5" />
                        Reporte del día
                    </Button>
                </div>
            </div>

            <div className="grid lg:grid-cols-[1fr,420px] gap-4 lg:gap-6">
                {/* LEFT: menu picker */}
                <div className="min-w-0">
                    {/* Search + scan + custom */}
                    <div className="flex flex-wrap gap-2 mb-3">
                        <div className="relative flex-1 min-w-[180px]">
                            <MagnifyingGlass
                                size={16}
                                weight="bold"
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                            />
                            <Input
                                data-testid="pos-search"
                                placeholder="Buscar artículo…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-11"
                            />
                        </div>
                        <Button
                            type="button"
                            data-testid="pos-scan-btn"
                            onClick={() => setScannerOpen(true)}
                            variant="outline"
                            className="rounded-full h-11 shrink-0"
                            title="Escanear código de barras o QR"
                        >
                            <Barcode size={16} weight="bold" className="sm:mr-1" />
                            <span className="hidden sm:inline">Escáner</span>
                        </Button>
                        <Button
                            type="button"
                            data-testid="pos-custom-btn"
                            onClick={() => setCustomDialog(true)}
                            variant="outline"
                            className="rounded-full h-11 shrink-0"
                        >
                            <Plus size={16} weight="bold" className="sm:mr-1" />
                            <span className="hidden sm:inline">Manual</span>
                        </Button>
                    </div>

                    {/* Categories */}
                    <div
                        data-testid="pos-categories"
                        className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-3"
                    >
                        {categories.map((c) => (
                            <button
                                key={c}
                                data-testid={`pos-cat-${c}`}
                                onClick={() => setActiveCat(c)}
                                className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 transition ${
                                    activeCat === c
                                        ? "bg-foreground text-background border-foreground"
                                        : "bg-white text-foreground/70 border-border hover:border-foreground"
                                }`}
                            >
                                {c === "all" ? "Todos" : c.replace(/-/g, " ")}
                            </button>
                        ))}
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                        {filtered.map((it) => (
                            <button
                                key={it.id}
                                data-testid={`pos-item-${it.id}`}
                                onClick={() => addToCart(it)}
                                className="group bg-white rounded-2xl border border-border overflow-hidden text-left hover:shadow-lg hover:-translate-y-0.5 transition"
                            >
                                <div className="aspect-[5/3] bg-muted overflow-hidden">
                                    <img
                                        src={it.image}
                                        alt=""
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                                <div className="p-3">
                                    <p className="font-bold text-sm leading-tight line-clamp-2">
                                        {it.name_en}
                                    </p>
                                    <p className="font-display font-black text-lg mt-1">
                                        ${it.price.toFixed(2)}
                                    </p>
                                </div>
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <p className="col-span-full text-sm text-muted-foreground">
                                Sin resultados.
                            </p>
                        )}
                    </div>
                </div>

                {/* RIGHT: ticket */}
                <Card className="p-4 lg:sticky lg:top-4 self-start" data-testid="pos-cart">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                            Ticket
                        </p>
                        {cart.length > 0 && (
                            <button
                                data-testid="pos-clear"
                                onClick={clear}
                                className="text-xs uppercase tracking-widest font-bold text-destructive hover:underline"
                            >
                                Limpiar
                            </button>
                        )}
                    </div>

                    {cart.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                            Agrega artículos del menú o usa &quot;Manual&quot; para un precio libre.
                        </p>
                    ) : (
                        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                            {cart.map((c, i) => (
                                <div
                                    key={i}
                                    data-testid={`pos-line-${i}`}
                                    className="flex items-center gap-2 py-2 border-b border-border last:border-b-0"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm truncate">
                                            {c.name} {c.custom && <span className="text-[10px] uppercase tracking-widest text-secondary">· manual</span>}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            ${c.price.toFixed(2)} ea
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            data-testid={`pos-dec-${i}`}
                                            onClick={() => dec(i)}
                                            className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-border hover:bg-muted"
                                        >
                                            <Minus size={12} weight="bold" />
                                        </button>
                                        <span className="font-bold w-6 text-center text-sm">
                                            {c.qty}
                                        </span>
                                        <button
                                            data-testid={`pos-inc-${i}`}
                                            onClick={() => inc(i)}
                                            className="h-7 w-7 inline-flex items-center justify-center rounded-full border border-border hover:bg-muted"
                                        >
                                            <Plus size={12} weight="bold" />
                                        </button>
                                    </div>
                                    <p className="font-display font-black text-sm w-16 text-right">
                                        ${(c.price * c.qty).toFixed(2)}
                                    </p>
                                    <button
                                        data-testid={`pos-remove-${i}`}
                                        onClick={() => remove(i)}
                                        className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive"
                                    >
                                        <Trash size={12} weight="bold" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Totals */}
                    <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm">
                        <Row k="Subtotal" v={`$${subtotal.toFixed(2)}`} />
                        <div className="grid grid-cols-2 gap-2 py-1">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                Descuento $
                                <Input
                                    data-testid="pos-discount-input"
                                    type="number" min="0" step="0.01" inputMode="decimal"
                                    value={discount}
                                    onChange={(e) => setDiscount(e.target.value)}
                                    placeholder="0.00"
                                    className="h-9 mt-1"
                                />
                            </label>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                                Propina $
                                <Input
                                    data-testid="pos-tip-input"
                                    type="number" min="0" step="0.01" inputMode="decimal"
                                    value={tip}
                                    onChange={(e) => setTip(e.target.value)}
                                    placeholder="0.00"
                                    className="h-9 mt-1"
                                />
                            </label>
                        </div>
                        {discountAmt > 0 && <Row k="Descuento" v={`-$${discountAmt.toFixed(2)}`} />}
                        <Row k="Impuesto (7.35%)" v={`$${tax.toFixed(2)}`} />
                        {tipAmt > 0 && <Row k="Propina" v={`$${tipAmt.toFixed(2)}`} />}
                        <div className="flex justify-between font-display font-black text-2xl pt-2 border-t border-border">
                            <span>Total</span>
                            <span data-testid="pos-total">${total.toFixed(2)}</span>
                        </div>
                    </div>

                    <Button
                        data-testid="pos-pay-btn"
                        disabled={cart.length === 0 || busy}
                        onClick={() => setPayOpen(true)}
                        className="w-full mt-4 h-12 rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest text-base"
                    >
                        Cobrar ${total.toFixed(2)}
                    </Button>
                </Card>
            </div>

            {/* Payment method dialog */}
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent data-testid="pos-pay-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            ¿Cómo paga el cliente?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-3xl font-display font-black text-primary">
                        ${total.toFixed(2)}
                    </p>
                    <div className="grid gap-3 mt-2">
                        <button
                            type="button"
                            data-testid="pay-cash"
                            disabled={busy}
                            onClick={() => { setPayOpen(false); setCashOpen(true); }}
                            className="flex items-center gap-4 p-5 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition"
                        >
                            <Money size={32} weight="fill" className="text-emerald-600" />
                            <div className="text-left">
                                <p className="font-display font-black text-lg">Efectivo</p>
                                <p className="text-xs text-muted-foreground">Calcula el cambio automáticamente</p>
                            </div>
                        </button>
                        <button
                            type="button"
                            data-testid="pay-card"
                            disabled={busy}
                            onClick={() => send("card")}
                            className="flex items-center gap-4 p-5 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition"
                        >
                            <CreditCard size={32} weight="fill" className="text-blue-600" />
                            <div className="text-left">
                                <p className="font-display font-black text-lg">Tarjeta (Terminal)</p>
                                <p className="text-xs text-muted-foreground">Cobras con tu terminal externo (Square, etc.) y registras la venta</p>
                            </div>
                        </button>
                        <button
                            type="button"
                            data-testid="pay-stripe"
                            disabled={busy}
                            onClick={() => send("stripe")}
                            className="flex items-center gap-4 p-5 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition"
                        >
                            <CreditCard size={32} weight="fill" className="text-violet-600" />
                            <div className="text-left">
                                <p className="font-display font-black text-lg">Tarjeta vía Stripe</p>
                                <p className="text-xs text-muted-foreground">Abre Stripe Checkout · el cliente ingresa su tarjeta</p>
                            </div>
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Cash dialog */}
            <Dialog open={cashOpen} onOpenChange={setCashOpen}>
                <DialogContent data-testid="pos-cash-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            Pago en efectivo
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Total</span>
                            <span className="font-display font-black text-xl">${total.toFixed(2)}</span>
                        </div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block">
                            Efectivo recibido
                        </label>
                        <Input
                            data-testid="cash-received"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={cashReceived}
                            onChange={(e) => setCashReceived(e.target.value)}
                            placeholder={total.toFixed(2)}
                            className="text-2xl font-display font-black h-14"
                            autoFocus
                        />
                        <div className="grid grid-cols-4 gap-2">
                            {[total, Math.ceil(total), Math.ceil(total / 5) * 5, Math.ceil(total / 10) * 10].map((q, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setCashReceived(q.toFixed(2))}
                                    className="py-2 rounded-lg border border-border text-sm font-bold hover:bg-muted"
                                >
                                    ${q.toFixed(2)}
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-between pt-2 border-t border-border">
                            <span className="text-sm font-bold">Cambio</span>
                            <span
                                data-testid="cash-change"
                                className="font-display font-black text-2xl text-emerald-600"
                            >
                                ${Math.max(0, (parseFloat(cashReceived || 0) - total)).toFixed(2)}
                            </span>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCashOpen(false)}
                            className="rounded-full"
                        >
                            Cancelar
                        </Button>
                        <Button
                            data-testid="cash-confirm"
                            disabled={busy || parseFloat(cashReceived || 0) < total}
                            onClick={() =>
                                send("cash", { cash_received: parseFloat(cashReceived || total) })
                            }
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                        >
                            Confirmar pago
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Custom item dialog */}
            <Dialog open={customDialog} onOpenChange={setCustomDialog}>
                <DialogContent data-testid="pos-custom-dialog" className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl">
                            Artículo manual
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            placeholder="Nombre"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            data-testid="custom-name"
                            autoFocus
                        />
                        <Input
                            placeholder="Precio"
                            type="number"
                            step="0.01"
                            value={customPrice}
                            onChange={(e) => setCustomPrice(e.target.value)}
                            data-testid="custom-price"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCustomDialog(false)} className="rounded-full">
                            Cancelar
                        </Button>
                        <Button data-testid="custom-add" onClick={addCustom} className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest">
                            Agregar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Universal scanner dialog (USB/Bluetooth keyboard mode + camera) */}
            <ScannerDialog
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onCode={onScan}
            />

            {/* Daily report + cash reconciliation */}
            <DailyReportDialog
                open={reportOpen}
                onClose={() => setReportOpen(false)}
            />

            {/* Receipt */}
            <Dialog open={!!lastReceipt} onOpenChange={(o) => !o && setLastReceipt(null)}>
                <DialogContent data-testid="pos-receipt" className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                            <Receipt size={22} weight="fill" className="text-emerald-600" />
                            Venta registrada
                        </DialogTitle>
                    </DialogHeader>
                    {lastReceipt && (
                        <div className="text-sm">
                            <p className="text-xs uppercase tracking-widest text-muted-foreground">Orden</p>
                            <p className="font-bold">{lastReceipt.id}</p>
                            <div className="my-3 border-t border-dashed border-border" />
                            {lastReceipt.items?.map((i, k) => (
                                <div key={k} className="flex justify-between">
                                    <span>{i.qty}× {i.name}</span>
                                    <span>${(i.line_total ?? i.price * i.qty).toFixed(2)}</span>
                                </div>
                            ))}
                            <div className="my-3 border-t border-dashed border-border" />
                            <Row k="Subtotal" v={`$${(lastReceipt.subtotal || 0).toFixed(2)}`} />
                            {lastReceipt.discount > 0 && <Row k="Descuento" v={`-$${lastReceipt.discount.toFixed(2)}`} />}
                            <Row k="Impuesto (7.35%)" v={`$${(lastReceipt.tax || 0).toFixed(2)}`} />
                            {lastReceipt.tip > 0 && <Row k="Propina" v={`$${lastReceipt.tip.toFixed(2)}`} />}
                            <div className="flex justify-between font-display font-black text-xl mt-1">
                                <span>Total</span><span>${(lastReceipt.total || 0).toFixed(2)}</span>
                            </div>
                            {lastReceipt.payment_method === "cash" && (
                                <>
                                    <Row k="Recibido" v={`$${(lastReceipt.cash_received || 0).toFixed(2)}`} />
                                    <div className="flex justify-between font-bold text-emerald-600 mt-1">
                                        <span>Cambio</span>
                                        <span>${(lastReceipt.change_due || 0).toFixed(2)}</span>
                                    </div>
                                </>
                            )}
                            <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
                                Pago: {lastReceipt.payment_method}
                            </p>
                            <div className="mt-4 p-3 bg-muted/60 rounded-xl text-xs">
                                <p className="font-bold uppercase tracking-widest text-[10px] text-muted-foreground mb-1">
                                    Enlace de seguimiento (para el cliente)
                                </p>
                                <code
                                    data-testid="receipt-track-url"
                                    className="block break-all text-[11px] text-foreground"
                                >
                                    {`${window.location.origin}/order/${lastReceipt.id}`}
                                </code>
                                <button
                                    type="button"
                                    data-testid="receipt-track-copy"
                                    onClick={() => {
                                        navigator.clipboard?.writeText(`${window.location.origin}/order/${lastReceipt.id}`);
                                        toast.success("Enlace copiado");
                                    }}
                                    className="mt-2 text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
                                >
                                    Copiar enlace
                                </button>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            onClick={() => smartPrint(lastReceipt, "customer")}
                            className="rounded-full"
                            data-testid="receipt-print"
                        >
                            🧾 Recibo cliente
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => smartPrint(lastReceipt, "kitchen")}
                            className="rounded-full"
                            data-testid="receipt-print-kitchen"
                        >
                            👨‍🍳 Ticket cocina
                        </Button>
                        <Button
                            onClick={() => setLastReceipt(null)}
                            className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                            data-testid="receipt-close"
                        >
                            <X size={14} weight="bold" className="mr-1" /> Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-bold">{v}</span>
        </div>
    );
}

function round2(n) {
    return Math.round(n * 100) / 100;
}
